import {Mapper} from '@s-ui/domain'

export class VehicleTypeStructuredDataSeoMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Object} deps.config
   * @param {Function} deps.structuredDataValueObjectFactory
   * */
  constructor({config, structuredDataValueObjectFactory}) {
    super()
    this._config = config
    this._structuredDataValueObjectFactory = structuredDataValueObjectFactory
    this._appName = this._config.get('APP_NAME')
    this._appNameCoches = this._config.get('APP_NAME_COCHES')
    this._offerTypeNew = this._config.get('search').OFFER_TYPE_NEW
    this._subscriptionVehicleStateNew = 'NEW'
    this._defaultCulture = this._config.get('DEFAULT_CULTURE')
  }

  setParams = ({
    bodyType,
    color,
    description,
    fuelType,
    image,
    make,
    model,
    name,
    offerCount,
    offerTypeId,
    price,
    productionDate,
    subscriptionVehicleState,
    url,
    adId,
    photos,
    km,
    cubicCapacity,
    hp,
    doors,
    seats,
    fuelTankCapacity,
    consumptionAverage,
    emissions,
    transmissionType,
    warrantyMonths,
    hasWarranty,
    hasOfficialWarranty,
    sellerDealer,
    year,
    version,
    dealerReviews,
    companyUrl,
    environmentalLabel
  }) => {
    this._bodyType = bodyType
    this._color = color
    this._description = description
    this._fuelType = fuelType
    this._image = image
    this._make = make
    this._model = model
    this._name = name
    this._offerCount = offerCount
    this._offerTypeId = offerTypeId
    this._price = price
    this._productionDate = productionDate
    this._subscriptionVehicleState = subscriptionVehicleState
    this._url = url
    this._adId = adId
    this._photos = photos
    this._km = km
    this._cubicCapacity = cubicCapacity
    this._hp = hp
    this._doors = doors
    this._seats = seats
    this._fuelTankCapacity = fuelTankCapacity
    this._consumptionAverage = consumptionAverage
    this._emissions = emissions
    this._transmissionType = transmissionType
    this._warrantyMonths = warrantyMonths
    this._hasWarranty = hasWarranty
    this._hasOfficialWarranty = hasOfficialWarranty
    this._sellerDealer = sellerDealer
    this._year = year
    this._version = version
    this._dealerReviews = dealerReviews
    this._companyUrl = companyUrl
    this._environmentalLabel = environmentalLabel

    return this
  }

  _replacePlaceholders(template, values) {
    if (!template) return ''

    return template.replace(/%\{(\w+)}/g, (placeholder, key) => {
      return values[key] !== undefined ? values[key] : placeholder
    })
  }

  _getItemCondition() {
    if (
      this._offerTypeId === this._offerTypeNew ||
      this._subscriptionVehicleState === this._subscriptionVehicleStateNew
    )
      return 'https://schema.org/NewCondition'
    return 'https://schema.org/UsedCondition'
  }

  _generateImagesArray() {
    if (!this._photos || this._photos.length === 0) {
      return this._image ? [this._image] : []
    }
    const mapByCulture = {
      'es-ES': {caption: `Foto de %{make} %{model} %{index}`}
    }

    const MAX_IMAGES = 20
    const limitedPhotos = this._photos.slice(0, MAX_IMAGES)

    return limitedPhotos.map((url, index) => {
      const captionTemplate = mapByCulture[this._defaultCulture].caption
      const caption = this._replacePlaceholders(captionTemplate, {
        make: this._make,
        model: this._model,
        index: index + 1
      })

      return {
        '@type': 'ImageObject',
        url,
        caption,
        width: 1200,
        height: 800
      }
    })
  }

  _generateVehicleEngine() {
    if (!this._cubicCapacity && !this._hp && !this._fuelType && !this._version) {
      return null
    }

    const engine = {
      '@type': 'EngineSpecification'
    }

    if (this._fuelType) {
      engine.engineType = this._fuelType
      engine.fuelType = this._fuelType
    }

    if (this._version) {
      engine.name = this._version
    }

    if (this._cubicCapacity) {
      engine.engineDisplacement = {
        '@type': 'QuantitativeValue',
        value: this._cubicCapacity,
        unitCode: 'CMQ',
        unitText: 'cc'
      }
    }

    if (this._hp) {
      engine.enginePower = {
        '@type': 'QuantitativeValue',
        value: this._hp,
        unitCode: 'N12',
        unitText: 'CV'
      }
    }

    return engine
  }

  _generateSellerObject() {
    if (!this._sellerDealer) return null

    const {name, phoneNumber, location, logoImageUrl, ratings, stockPageUrl} = this._sellerDealer

    if (!name) return null

    const seller = {
      '@type': 'AutoDealer',
      name
    }

    if (phoneNumber) {
      seller.telephone = phoneNumber
    }

    if (this._companyUrl && stockPageUrl) {
      seller['@id'] = `${this._companyUrl}${stockPageUrl}`
    }

    if (logoImageUrl) {
      seller.image = logoImageUrl
    }

    if (location && location.address && location.city) {
      seller.address = {
        '@type': 'PostalAddress',
        streetAddress: location.address,
        addressLocality: location.city,
        addressRegion: location.province,
        postalCode: location.postalCode,
        addressCountry: 'ES'
      }
    }

    if (ratings && ratings.average && ratings.totalReviews) {
      seller.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: ratings.average,
        reviewCount: ratings.totalReviews,
        bestRating: 5
      }
    }

    return seller
  }

  _generateOfferObject() {
    const offer = {
      '@type': 'Offer',
      '@id': `${this._url}`,
      price: this._price,
      priceCurrency: this._config.get('DEFAULT_CURRENCY'),
      itemCondition: this._getItemCondition(),
      availability: 'https://schema.org/InStock',
      url: this._url
    }

    const seller = this._generateSellerObject()
    if (seller) {
      offer.seller = seller
    }

    if (this._hasWarranty && this._warrantyMonths) {
      const uniTextMap = {
        'es-ES': {
          unitText: 'meses'
        }
      }
      const warrantyTextMap = {
        'es-ES': {
          warrantyScopeOfficial: 'Garantía oficial',
          warrantyScopeIncluded: 'Garantía incluida'
        }
      }
      const warrantyScope = this._hasOfficialWarranty
        ? warrantyTextMap[this._defaultCulture].warrantyScopeOfficial
        : warrantyTextMap[this._defaultCulture].warrantyScopeIncluded

      offer.warranty = {
        '@type': 'WarrantyPromise',
        durationOfWarranty: {
          '@type': 'QuantitativeValue',
          value: this._warrantyMonths,
          unitCode: 'MON',
          unitText: uniTextMap[this._defaultCulture].unitText
        },
        warrantyScope
      }
    }

    return offer
  }

  _getTransmissionType() {
    if (!this._transmissionType) return undefined

    const transmissionLower = this._transmissionType.toLowerCase()

    if (transmissionLower.includes('automatic')) return 'Automatic'
    if (transmissionLower.includes('manual')) return 'Manual'

    return this._transmissionType
  }

  _generateAdditionalProperties() {
    if (!this._environmentalLabel) return null

    const properties = []
    const tracking = this._config.get('tracking')

    // Safely access tracking config - may not be same ids in motos
    if (!tracking.TRACKING_ENVIRONMENTAL_LABELS_FILTERS_TRANSLATIONS) return null

    const environmentalLabelsConfig = tracking.TRACKING_ENVIRONMENTAL_LABELS_FILTERS_TRANSLATIONS
    const defaultCulture = this._config.get('DEFAULT_CULTURE')

    const labelTranslation = environmentalLabelsConfig[this._environmentalLabel]
    const labelValue = labelTranslation ? labelTranslation[defaultCulture] : null

    if (labelValue) {
      properties.push({
        '@type': 'PropertyValue',
        name: 'Etiqueta Medioambiental DGT',
        value: labelValue
      })
    }

    return properties.length > 0 ? properties : null
  }

  map = () => {
    const isCoches = this._appName === this._appNameCoches
    const vehicleType = isCoches ? 'Car' : 'Motorcycle'

    const vehicleTypeSchema = {
      '@context': 'https://schema.org',
      '@type': [vehicleType, 'Product'],
      '@id': this._url,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': this._url
      },
      name: this._name,
      brand: {
        '@type': 'Brand',
        name: this._make
      },
      model: this._model,
      url: this._url,
      image: this._generateImagesArray(),
      productionDate: this._productionDate,
      vehicleModelDate: this._year,
      modelDate: this._year,
      bodyType: this._bodyType,
      color: this._color,
      itemCondition: this._getItemCondition()
    }

    if (this._doors) {
      vehicleTypeSchema.numberOfDoors = this._doors
    }

    if (this._seats) {
      vehicleTypeSchema.seatingCapacity = this._seats
    }

    const transmission = this._getTransmissionType()
    if (transmission) {
      vehicleTypeSchema.vehicleTransmission = transmission
    }

    if (this._fuelType) {
      vehicleTypeSchema.fuelType = this._fuelType
    }

    if (this._km) {
      vehicleTypeSchema.mileageFromOdometer = {
        '@type': 'QuantitativeValue',
        value: this._km,
        unitCode: 'KMT',
        unitText: 'km'
      }
    }

    const engine = this._generateVehicleEngine()
    if (engine) {
      vehicleTypeSchema.vehicleEngine = engine
    }

    if (this._fuelTankCapacity) {
      const cultureMap = {
        'es-ES': {unitText: 'litros'}
      }

      vehicleTypeSchema.fuelCapacity = {
        '@type': 'QuantitativeValue',
        value: this._fuelTankCapacity,
        unitCode: 'LTR',
        unitText: cultureMap[this._defaultCulture].unitText
      }
    }

    if (this._consumptionAverage) {
      vehicleTypeSchema.fuelEfficiency = {
        '@type': 'QuantitativeValue',
        value: this._consumptionAverage,
        unitCode: 'L',
        unitText: 'l/100km'
      }
    }

    // emissions can be 0 for electric vehicles, so check !== undefined
    if (this._emissions !== undefined && this._emissions !== null) {
      vehicleTypeSchema.emissionsCO2 = {
        '@type': 'QuantitativeValue',
        value: this._emissions,
        unitCode: 'GM',
        unitText: 'g/km'
      }
    }

    const additionalProperties = this._generateAdditionalProperties()
    if (additionalProperties) {
      vehicleTypeSchema.additionalProperty = additionalProperties
    }

    vehicleTypeSchema.offers = this._generateOfferObject()

    return this._structuredDataValueObjectFactory(vehicleTypeSchema)
  }
}
